import { type VariantProps, cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

const bannerVariants = cva(
  'flex w-full items-center gap-3 px-4 py-3 text-sm',
  {
    variants: {
      variant: {
        warning: 'bg-amber-50 text-amber-900 border-b border-amber-200',
        info: 'bg-blue-50 text-blue-900 border-b border-blue-200',
        error: 'bg-red-50 text-red-900 border-b border-red-200',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

export interface BannerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof bannerVariants> {
  onDismiss?: () => void;
  dismissible?: boolean;
  icon?: React.ReactNode;
}

export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  ({ className, variant, children, onDismiss, dismissible = false, icon, ...props }, ref) => {
    const role = variant === 'error' ? 'alert' : 'status';

    return (
      <div
        ref={ref}
        role={role}
        aria-live="polite"
        className={cn(bannerVariants({ variant }), className)}
        {...props}
      >
        {icon && <span className="shrink-0">{icon}</span>}
        <div className="flex-1">{children}</div>
        {dismissible && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }
);

Banner.displayName = 'Banner';
